import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import api from "../api/Api.js";

const DriverTripPage = () => {
  const [flatData, setFlatData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tripIdFilter, setTripIdFilter] = useState("");

  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(
    today.toISOString().split("T")[0],
  );

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchAllData();
  }, [selectedDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, tripIdFilter]);

  const formatTime = (timeStr) => {
    if (!timeStr) return "TBD";

    return new Date(timeStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const fetchAllData = async () => {
    if (!selectedDate) return;
    setIsLoading(true);

    const dateObj = new Date(selectedDate);
    const date = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = String(dateObj.getFullYear());

    try {
      // Call the NEW optimized single endpoint
      const resp = await api.get(
        `/adminmodTracking/getAllTrackingData/${date}/${month}/${year}`,
      );

      const rawData = resp.data?.data || [];

      const newFlatData = rawData.map((row, idx) => ({
        Journey_date: row.Journey_date,
        driver_id: row.driver_id,
        driver_regno: row.driver_regno,
        driver_fstname: row.driver_fstname,
        driver_mdlname: row.driver_mdlname,
        driver_lstname: row.driver_lstname,
        driver_phno: row.driver_phno,

        tripData: row.cust_bookingid
          ? {
              cust_bookingid: row.cust_bookingid,
              drv_id: row.driver_id,
              pickup_location: row.pickup_location,
              dropoff_location: row.dropoff_location,
              Pickup_time: row.Pickup_time,
              dropoff_time: row.dropoff_time,
              confirmation_id: row.confirmation_id,
              customer_name: row.customer_name,
              cust_mobileno: row.cust_mobileno,
              enqref_no: row.enqref_no,
            }
          : null,
        tripIndex: idx + 1,
      }));

      setFlatData(newFlatData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load tracking details");
      setFlatData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // const fetchAllData = async () => {
  //   if (!selectedDate) return;
  //   setIsLoading(true);

  //   const dateObj = new Date(selectedDate);
  //   const date = String(dateObj.getDate()).padStart(2, "0");
  //   const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  //   const year = String(dateObj.getFullYear());

  //   try {
  //     const resp = await api.get(
  //       `/adminmodTracking/getDuityData/${date}/${month}/${year}`,
  //     );
  //     const drivers = resp.data?.data || [];
  //     console.log("📍 Step 1: Initial Drivers Data:", drivers);

  //     const tripsPromises = drivers.map(async (driver) => {
  //       try {
  //         const tripResp = await api.get(
  //           `/adminmodTracking/sendTripdtls/${driver.acpt_date}/${driver.driver_id}`,
  //         );
  //         return { driver, trips: tripResp.data?.data || [] };
  //       } catch {
  //         return { driver, trips: [] };
  //       }
  //     });

  //     const driversWithTrips = await Promise.all(tripsPromises);
  //     console.log("📍 Step 2: Unflattened Drivers with Trips:", driversWithTrips);

  //     const newFlatData = [];
  //     driversWithTrips.forEach(({ driver, trips }) => {
  //       if (trips.length === 0) {
  //         newFlatData.push({ ...driver, tripData: null });
  //       } else {
  //         trips.forEach((trip, idx) => {
  //           newFlatData.push({ ...driver, tripData: trip, tripIndex: idx + 1 });
  //         });
  //       }
  //     });
  //     console.log("📍 Step 3: Final Flattened Data:", newFlatData);

  //     setFlatData(newFlatData);
  //   } catch {
  //     toast.error("Failed to load tracking details");
  //     setFlatData([]);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // ── Filtering ──────────────────────────────────────────────

  const filteredData = flatData.filter((row) => {
    const term = searchTerm.toLowerCase();
    const tripTerm = tripIdFilter.toLowerCase();

    const fullName =
      `${row.driver_fstname || ""} ${row.driver_mdlname || ""} ${row.driver_lstname || ""}`.toLowerCase();

    const matchesSearch =
      !term ||
      fullName.includes(term) ||
      row.driver_regno?.toLowerCase().includes(term) ||
      row.tripData?.pickup_location?.toLowerCase().includes(term) ||
      row.tripData?.dropoff_location?.toLowerCase().includes(term);

    const matchesTripId =
      !tripTerm ||
      row.tripData?.confirmation_id?.toLowerCase().includes(tripTerm);

    return matchesSearch && matchesTripId;
  });

  // ── Pagination ─────────────────────────────────────────────
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const handleMapView = (cust_bookingid, drv_id) => {
    // This keeps the Admin on the Admin site, just moving to the Map page
    const trackingUrl = `${window.location.origin}/driver-tracking-map?cust_bookingid=${cust_bookingid}&drv_id=${drv_id}`;

    window.open(trackingUrl, "_blank", "noopener,noreferrer");
  };

  const formatDate = (dateStr) =>
    dateStr
      ? new Date(dateStr)
          .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          .replace(/ /g, "-")
      : "-";

  // Page numbers: 1, ..., cur-1, cur, cur+1, ..., last
  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...");
      }
    }
    return pages;
  };

  const driversCount = new Set(filteredData.map((r) => r.driver_id)).size;
  const tripsCount = filteredData.filter((r) => r.tripData).length;

  // ── Excel Export ───────────────────────────────────────────
  const handleExportExcel = () => {
    if (filteredData.length === 0) {
      toast.error("No data to export for this date.");
      return;
    }

    const excelData = filteredData.map((row, index) => ({
      "Sl No": index + 1,
      "Trip Date": formatDate(row.Journey_date),
      "Driver Name":
        `${row.driver_fstname || ""} ${row.driver_mdlname || ""} ${row.driver_lstname || ""}`.trim(),
      "Driver Reg No": row.driver_regno || "N/A",
      "Driver Phone": row.driver_phno || "N/A",
      "Customer Name": row.tripData?.customer_name || "Guest",
      "Customer Phone": row.tripData?.cust_mobileno || "N/A",
      "Trip ID": row.tripData?.confirmation_id || "N/A",
      "Ref No": row.tripData?.enqref_no || "N/A",
      "Pickup Location": row.tripData?.pickup_location || "N/A",
      "Pickup Time": row.tripData?.Pickup_time
        ? formatTime(row.tripData.Pickup_time)
        : "TBD",
      "Drop Location": row.tripData?.dropoff_location || "N/A",
      "Drop Time": row.tripData?.dropoff_time
        ? formatTime(row.tripData.dropoff_time)
        : "TBD",
    }));

    // 2. Create the Worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Driver_Trips");

    // 3. Set column widths
    const wscols = [
      { wch: 6 }, // Sl No
      { wch: 15 }, // Trip Date
      { wch: 25 }, // Driver Name
      { wch: 15 }, // Driver Reg
      { wch: 15 }, // Driver Phone
      { wch: 20 }, // Customer Name
      { wch: 15 }, // Customer Phone
      { wch: 20 }, // Trip ID
      { wch: 15 }, // Ref No
      { wch: 35 }, // Pickup Loc
      { wch: 15 }, // Pickup Time
      { wch: 35 }, // Drop Loc
      { wch: 15 }, // Drop Time
    ];
    worksheet["!cols"] = wscols;

    // 4. Download the file
    XLSX.writeFile(workbook, `Driver_Trips_${selectedDate}.xlsx`);
    toast.success("Excel exported successfully!");
  };

  return (
    <div className="container-fluid py-2 px-2">
      {/* ── Page Header ── */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center rounded-3 shadow-sm flex-shrink-0"
            style={{
              width: 38,
              height: 38,
              background: "linear-gradient(135deg,#1a56db,#3b82f6)",
            }}
          >
            <i className="fa fa-map-marker text-white fs-6"></i>
          </div>
          <div>
            <h5 className="mb-0 fw-bold text-dark lh-1">Driver Location Tracking</h5>
            <small className="text-muted">
              Monitor real-time driver locations &amp; trips
            </small>
          </div>
        </div>

        {/* EXCEL BUTTON */}
        {/* <button
          onClick={handleExportExcel}
          disabled={isLoading || filteredData.length === 0}
          className="btn btn-sm btn-success d-flex align-items-center gap-2 shadow-sm fw-medium px-3"
        >
          <i className="fa fa-file-excel-o"></i>
          Export to Excel
        </button> */}
      </div>

      {/* ── Main Card ── */}
        <div className="card border-0 shadow-sm rounded-3 overflow-hidden">

       {/* ── Control Bar ── */}
        <div className="card-header bg-white border-bottom py-2 px-2">
          <div 
            className="row g-4 align-items-end flex-nowrap pb-1" 
            style={{ overflowX: "auto", scrollbarWidth: "none" }}
          >
            {/* Date */}
            <div className="col-auto">
              <label
                className="form-label fw-bold text-muted mb-1 text-nowrap"
                style={{
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: ".5px",
                }}
              >
                <i className="fa fa-calendar me-1"></i>Date
              </label>
              <input
                type="date"
                className="form-control form-control-sm border-0 border-bottom rounded-0 px-1 shadow-none"
                style={{ width: 150, fontSize: "0.85rem", backgroundColor: "transparent", borderColor: "#ced4da" }}
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>

            {/* Driver / Location Search */}
            <div className="col-auto">
              <label
                className="form-label fw-bold text-muted mb-1 text-nowrap"
                style={{
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: ".5px",
                }}
              >
                <i className="fa fa-search me-1"></i>Driver / Location
              </label>
              <div
                className="input-group input-group-sm border-bottom"
                style={{ width: 280, flexWrap: "nowrap", borderColor: "#ced4da" }}
              >
                <span className="input-group-text bg-transparent border-0 text-muted px-1">
                  <i className="fa fa-search" style={{ fontSize: "0.8rem" }}></i>
                </span>
                <input
                  type="text"
                  className="form-control border-0 px-1 shadow-none bg-transparent"
                  placeholder="Name, Reg No, Location..."
                  style={{ fontSize: "0.85rem" }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="btn border-0 text-danger shadow-none bg-transparent px-2"
                    onClick={() => setSearchTerm("")}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Trip ID Filter */}
            <div className="col-auto">
              <label
                className="form-label fw-bold text-muted mb-1 text-nowrap"
                style={{
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: ".5px",
                }}
              >
                <i className="fa fa-tag me-1"></i>Trip ID
              </label>
              <div
                className="input-group input-group-sm border-bottom"
                style={{ width: 200, flexWrap: "nowrap", borderColor: "#ced4da" }}
              >
                <span className="input-group-text bg-transparent border-0 text-muted px-1">
                  <i className="fa fa-hashtag" style={{ fontSize: "0.8rem" }}></i>
                </span>
                <input
                  type="text"
                  className="form-control border-0 px-1 shadow-none bg-transparent"
                  placeholder="Enter Trip ID..."
                  style={{ fontSize: "0.85rem" }}
                  value={tripIdFilter}
                  onChange={(e) => setTripIdFilter(e.target.value)}
                />
                {tripIdFilter && (
                  <button
                    className="btn border-0 text-danger shadow-none bg-transparent px-2"
                    onClick={() => setTripIdFilter("")}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Stats Pills */}
            {!isLoading && flatData.length > 0 && (
              <div className="col-auto ms-auto pe-2">
                <div className="d-flex gap-2 text-nowrap">
                  {[
                    { num: driversCount, lbl: "Drivers", color: "primary" },
                    { num: tripsCount, lbl: "Trips", color: "success" },
                    { num: filteredData.length, lbl: "Records", color: "info" },
                  ].map(({ num, lbl, color }) => (
                    <div
                      key={lbl}
                      className={`text-center border border-${color}-subtle rounded-3 px-2 py-1 bg-${color} bg-opacity-10`}
                      style={{ minWidth: 65 }}
                    >
                      <div
                        className={`fw-bold text-${color} lh-1`}
                        style={{ fontSize: "0.9rem" }}
                      >
                        {num}
                      </div>
                      <div
                        className={`text-${color} mt-1 fw-semibold`}
                        style={{
                          fontSize: "0.55rem",
                          textTransform: "uppercase",
                          letterSpacing: ".5px",
                        }}
                      >
                        {lbl}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        <div className="table-responsive shadow-sm border rounded-3 overflow-hidden bg-white mb-2">
          <table
            className="table table-hover align-middle mb-0"
            style={{ fontSize: "0.85rem" }}
          >
            <thead
              style={{
                background: "linear-gradient(135deg, #1a56db, #2563eb)",
              }}
            >
              <tr>
                {[
                  { label: "#", w: "4%", c: true },
                  { label: "Trip Date", w: "10%", c: false },
                  { label: "Driver Details", w: "18%", c: false },
                  { label: "Customer Details", w: "16%", c: false },
                  { label: "Trip Details", w: "12%", c: false },
                  { label: "Pickup Location", w: "17%", c: false },
                  { label: "Drop Location", w: "18%", c: false },
                  { label: "View Map", w: "5%", c: true },
                ].map(({ label, w, c }) => (
                  <th
                    key={label}
                    className={`py-3 fw-semibold ${c ? "text-center" : ""}`}
                    style={{
                      width: w,
                      color: "#ffffff",
                      background: "transparent",
                      fontSize: "0.75rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="border-top-0">
              {/* Loading State */}
              {isLoading && (
                <tr>
                  <td colSpan="8" className="text-center py-5 bg-light">
                    <div
                      className="spinner-border spinner-border-sm text-primary mb-2"
                      role="status"
                    ></div>
                    <div
                      className="text-muted fw-semibold"
                      style={{ fontSize: "0.85rem" }}
                    >
                      Loading driver data…
                    </div>
                  </td>
                </tr>
              )}

              {/* Empty State */}
              {!isLoading && currentItems.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-5 bg-light">
                    <div
                      className="d-inline-flex align-items-center justify-content-center bg-white rounded-circle shadow-sm mb-3"
                      style={{ width: 60, height: 60 }}
                    >
                      <i className="fa fa-inbox fa-xl text-secondary opacity-50"></i>
                    </div>
                    <div
                      className="fw-bold text-dark"
                      style={{ fontSize: "0.95rem" }}
                    >
                      No records found
                    </div>
                    <div
                      className="text-muted mt-1"
                      style={{ fontSize: "0.8rem" }}
                    >
                      Try adjusting filters or pick another date
                    </div>
                  </td>
                </tr>
              )}

              {/* Data Rows */}
              {!isLoading &&
                currentItems.map((row, index) => (
                  <tr
                    key={index}
                    className="border-bottom"
                    style={{ transition: "background-color 0.2s" }}
                  >
                    {/* Serial */}
                    <td className="text-center fw-bold text-secondary">
                      {indexOfFirstItem + index + 1}
                    </td>

                    {/* Date */}
                    <td className="text-muted fw-medium text-nowrap">
                      {formatDate(row.Journey_date)}
                    </td>

                    {/* Driver Name & Details */}
                    <td>
                      <div
                        className="fw-bold text-dark mb-1"
                        style={{ fontSize: "0.9rem" }}
                      >
                        {row.driver_fstname} {row.driver_mdlname}{" "}
                        {row.driver_lstname}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle px-2 py-1"
                          style={{ fontSize: "0.7rem", letterSpacing: "0.3px" }}
                        >
                          {row.driver_regno || "N/A"}
                        </span>
                        <span
                          className="text-secondary"
                          style={{ fontSize: "0.75rem" }}
                        >
                          <i className="fa fa-phone me-1 text-muted"></i>
                          {row.driver_phno || "N/A"}
                        </span>
                      </div>
                    </td>

                    {/* Trip Columns Container */}
                    {row.tripData ? (
                      <>
                        {/* Customer Details */}
                        <td>
                          <div
                            className="fw-semibold text-dark mb-1"
                            style={{ fontSize: "0.85rem" }}
                          >
                            <i className="fa fa-user-circle text-muted me-1"></i>
                            {row.tripData.customer_name || "Guest"}
                          </div>
                          <div
                            className="text-secondary"
                            style={{ fontSize: "0.75rem" }}
                          >
                            <i className="fa fa-phone me-1 text-muted"></i>
                            {row.tripData.cust_mobileno || "N/A"}
                          </div>
                        </td>

                        {/* Trip ID */}
                        <td>
                          <span
                            className="badge bg-success bg-opacity-10 text-success border border-success-subtle px-2 py-1 mb-1 d-inline-block"
                            style={{
                              fontSize: "0.75rem",
                              letterSpacing: "0.3px",
                            }}
                          >
                            {row.tripData.confirmation_id}
                          </span>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.7rem", fontWeight: "500" }}
                          >
                            Ref: {row.tripData.enqref_no || "N/A"}
                          </div>
                        </td>

                        {/* Pickup */}
                        <td
                          title={row.tripData.pickup_location}
                          style={{ maxWidth: 180 }}
                        >
                          <div
                            className="text-truncate fw-medium text-dark"
                            style={{ fontSize: "0.85rem" }}
                          >
                            <i
                              className="fa fa-circle text-success me-2"
                              style={{
                                fontSize: "0.5rem",
                                verticalAlign: "middle",
                              }}
                            ></i>
                            {row.tripData.pickup_location || "-"}
                          </div>
                          {/* ADDED TIME DISPLAY */}
                          <div
                            className="text-muted mt-1 ms-3"
                            style={{ fontSize: "0.75rem" }}
                          >
                            <i className="fa fa-clock-o me-1"></i>
                            {formatTime(row.tripData.Pickup_time)}
                          </div>
                        </td>

                        {/* Drop */}
                        <td
                          title={row.tripData.dropoff_location}
                          style={{ maxWidth: 180 }}
                        >
                          <div
                            className="text-truncate fw-medium text-dark"
                            style={{ fontSize: "0.85rem" }}
                          >
                            <i
                              className="fa fa-map-marker text-danger me-2"
                              style={{
                                fontSize: "0.8rem",
                                verticalAlign: "middle",
                              }}
                            ></i>
                            {row.tripData.dropoff_location || "-"}
                          </div>
                          {/* ADDED TIME DISPLAY */}
                          <div
                            className="text-muted mt-1 ms-3"
                            style={{ fontSize: "0.75rem" }}
                          >
                            <i className="fa fa-clock-o me-1"></i>
                            {formatTime(row.tripData.dropoff_time)}
                          </div>
                        </td>

                        {/* Map Button */}
                        <td className="text-center align-middle">
                          <button
                            className="btn btn-outline-danger btn-sm rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm"
                            style={{
                              width: 34,
                              height: 34,
                              transition: "all 0.2s",
                            }}
                            title="View on Map"
                            onClick={() =>
                              handleMapView(
                                row.tripData.cust_bookingid,
                                row.tripData.drv_id,
                              )
                            }
                          >
                            <i
                              className="fa fa-location-arrow"
                              style={{ fontSize: "0.9rem" }}
                            ></i>
                          </button>
                        </td>
                      </>
                    ) : (
                      <td
                        colSpan="5"
                        className="text-center bg-light rounded-end"
                      >
                        <span
                          className="text-warning fw-bold"
                          style={{ fontSize: "0.85rem" }}
                        >
                          <i className="fa fa-exclamation-triangle me-2"></i>No
                          Trips Assigned
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!isLoading && filteredData.length > itemsPerPage && (
          <div className="card-footer bg-light border-top d-flex flex-wrap justify-content-between align-items-center py-2 px-3 gap-2">
            {/* Record Count Info */}
            <small className="text-muted">
              Showing{" "}
              <strong className="text-dark">{indexOfFirstItem + 1}</strong>–
              <strong className="text-dark">
                {Math.min(indexOfLastItem, filteredData.length)}
              </strong>{" "}
              of <strong className="text-dark">{filteredData.length}</strong>{" "}
              records
            </small>

            {/* Page Controls */}
            <ul className="pagination pagination-sm mb-0">
              {/* Prev */}
              <li
                className={`page-item me-1 ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link rounded-2"
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <i
                    className="fa fa-chevron-left"
                    style={{ fontSize: "0.62rem" }}
                  ></i>
                </button>
              </li>

              {/* Page Numbers */}
              {getPageNumbers().map((p, idx) =>
                p === "..." ? (
                  <li key={`e-${idx}`} className="page-item disabled">
                    <span className="page-link border-0 bg-transparent px-1 text-muted">
                      ···
                    </span>
                  </li>
                ) : (
                  <li
                    key={p}
                    className={`page-item mx-1 ${currentPage === p ? "active" : ""}`}
                  >
                    <button
                      className="page-link rounded-2"
                      style={
                        currentPage === p
                          ? {
                              background: "#1a56db",
                              borderColor: "#1a56db",
                              color: "#fff",
                            }
                          : { fontSize: "0.8rem" }
                      }
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  </li>
                ),
              )}

              {/* Next */}
              <li
                className={`page-item ms-1 ${currentPage === totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link rounded-2"
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <i
                    className="fa fa-chevron-right"
                    style={{ fontSize: "0.62rem" }}
                  ></i>
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverTripPage;